// Pure helpers for the map view. Input records come from the metadata
// server (server/lib/normalize.js). All times are ISO strings in UTC.

export const NIGHT_SUN_ALTITUDE_DEG = -6; // civil twilight
export const DEFAULT_MAX_GAP_MINUTES = 120;

const rad = Math.PI / 180;

// Sun altitude in degrees for a UTC date at lat/lon. Low-precision
// algorithm (about 0.3 degrees), plenty for a day/night decision.
export function sunAltitude(date, lat, lon) {
  const days = date.getTime() / 86400000 - 10957.5; // days since J2000.0
  const meanLongitude = (280.46 + 0.9856474 * days) % 360;
  const meanAnomaly = ((357.528 + 0.9856003 * days) % 360) * rad;
  const eclipticLongitude =
    (meanLongitude +
      1.915 * Math.sin(meanAnomaly) +
      0.02 * Math.sin(2 * meanAnomaly)) *
    rad;
  const obliquity = (23.439 - 0.0000004 * days) * rad;
  const rightAscension = Math.atan2(
    Math.cos(obliquity) * Math.sin(eclipticLongitude),
    Math.cos(eclipticLongitude),
  );
  const declination = Math.asin(
    Math.sin(obliquity) * Math.sin(eclipticLongitude),
  );
  const siderealHours = (18.697374558 + 24.06570982441908 * days) % 24;
  const localSidereal = (siderealHours * 15 + lon) * rad;
  const hourAngle = localSidereal - rightAscension;
  const altitude = Math.asin(
    Math.sin(lat * rad) * Math.sin(declination) +
      Math.cos(lat * rad) * Math.cos(declination) * Math.cos(hourAngle),
  );
  return altitude / rad;
}

export function dayOrNight(date, lat, lon) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "unknown";
  if (typeof lat !== "number" || typeof lon !== "number") return "unknown";
  return sunAltitude(date, lat, lon) < NIGHT_SUN_ALTITUDE_DEG ? "night" : "day";
}

const parseIso = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Best available timestamp for a video, most trustworthy first.
export function resolveTime(video) {
  const candidates = [
    ["gps", video.gps?.time],
    ["filename", video.filenameTime],
    ["container", video.createDate],
    ["file-modified", video.modifiedAt],
  ];
  for (const [source, value] of candidates) {
    const date = parseIso(value);
    if (date) return { time: date, source };
  }
  return { time: null, source: null };
}

// "corrupt": ExifTool could not read the file.
// "no-gps": readable but no usable coordinates (metadata issue).
// "ok": has coordinates.
export function classify(video) {
  if (video.error || video.size === 0) return "corrupt";
  if (!hasPosition(video.gps)) return "no-gps";
  return "ok";
}

// Database status as computed by the backend, or derived from `db` when
// an older backend sends only the rows summary.
export function dbStatusOf(video) {
  if (video?.dbStatus) return video.dbStatus;
  const db = video?.db;
  if (!db) return "unknown";
  if (!db.registered) return "missing";
  if (Array.isArray(db.errors) && db.errors.length > 0) return "error";
  if (db.pending > 0) return "pending";
  return "processed";
}

const hasPosition = (gps) =>
  !!gps &&
  typeof gps.lat === "number" &&
  typeof gps.lon === "number" &&
  Number.isFinite(gps.lat) &&
  Number.isFinite(gps.lon) &&
  !(gps.lat === 0 && gps.lon === 0);

const lerp = (a, b, t) => a + (b - a) * t;

// Estimate where an un-located video was recorded from the nearest
// located videos before and after it in time.
function estimateFromNeighbours(target, located, maxGapMs) {
  const t = target.time.getTime();
  let before = null;
  let after = null;
  for (const item of located) {
    const itemTime = item.time.getTime();
    if (itemTime <= t && (!before || itemTime > before.time.getTime())) {
      before = item;
    }
    if (itemTime >= t && (!after || itemTime < after.time.getTime())) {
      after = item;
    }
  }
  const okBefore = before && t - before.time.getTime() <= maxGapMs;
  const okAfter = after && after.time.getTime() - t <= maxGapMs;

  if (okBefore && okAfter) {
    const span = after.time.getTime() - before.time.getTime();
    const fraction = span === 0 ? 0 : (t - before.time.getTime()) / span;
    return {
      lat: lerp(before.position.lat, after.position.lat, fraction),
      lon: lerp(before.position.lon, after.position.lon, fraction),
      estimated: true,
      method: "interpolated",
      basedOn: [before.name, after.name],
    };
  }
  const neighbour = okBefore ? before : okAfter ? after : null;
  if (neighbour) {
    return {
      lat: neighbour.position.lat,
      lon: neighbour.position.lon,
      estimated: true,
      method: okBefore ? "after-previous" : "before-next",
      basedOn: [neighbour.name],
    };
  }
  return null;
}

export const dateKeyOf = (date) =>
  date ? date.toISOString().slice(0, 10) : "unknown";

// Minutes east of UTC for the dataset's `timeZone` ("+05:30", "UTC",
// "local" = this browser's zone -> null). Undefined keeps UTC, which is
// what older datasets implied.
export function offsetMinutesOf(timeZone) {
  if (timeZone === undefined) return 0;
  if (timeZone === null || timeZone === "" || timeZone === "local") return null;
  if (/^(utc|gmt|z)$/i.test(timeZone)) return 0;
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec(String(timeZone).trim());
  if (!m) return 0;
  const minutes = Number(m[2]) * 60 + Number(m[3]);
  return m[1] === "-" ? -minutes : minutes;
}

const pad2 = (n) => String(n).padStart(2, "0");

// Calendar day of an instant in the given zone (null = browser zone).
export function localDateKey(value, offsetMinutes = 0) {
  const date = value instanceof Date ? value : parseIso(value);
  if (!date) return null;
  if (offsetMinutes === null) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  }
  return new Date(date.getTime() + offsetMinutes * 60000).toISOString().slice(0, 10);
}

const NAME_STAMP_RE = /(20\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/;

// The recording date is the one in the file name (20261006103000_x.MP4 ->
// 2026-10-06): that is the date the operator knows the video by, and it is
// not subject to a wrong dashcam GPS clock or a time zone shift.
export function nameDateKey(name) {
  const m = NAME_STAMP_RE.exec(name || "");
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

export function nameDateTime(name) {
  const m = NAME_STAMP_RE.exec(name || "");
  return m ? `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}` : null;
}

// {dateKey: count} over items, by `dateKey` (recorded) or `processedKey`.
export function countByKey(items, field = "dateKey") {
  const counts = {};
  (items || []).forEach((item) => {
    const key = item?.[field];
    if (key && key !== "unknown") counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

// Turns raw metadata records into plottable items.
export function analyzeVideos(videos, options = {}) {
  const maxGapMs =
    (options.maxGapMinutes ?? DEFAULT_MAX_GAP_MINUTES) * 60 * 1000;
  const offset = offsetMinutesOf(options.timeZone);

  const items = (videos || []).map((video) => {
    const { time, source } = resolveTime(video);
    const status = classify(video);
    const position = hasPosition(video.gps)
      ? { lat: video.gps.lat, lon: video.gps.lon, estimated: false, method: "gps" }
      : null;
    const processedAt = parseIso(video.db?.processedOn);
    return {
      ...video,
      time,
      timeSource: source,
      status,
      dbStatus: dbStatusOf(video),
      position,
      // Recorded day from the file name; the resolved time is only a fallback.
      dateKey: nameDateKey(video.name) ?? (time ? localDateKey(time, offset) : "unknown"),
      processedAt,
      processedKey: processedAt ? localDateKey(processedAt, offset) : null,
      period: "unknown",
    };
  });

  const located = items.filter((item) => item.position && item.time);
  items.forEach((item) => {
    if (!item.position && item.time) {
      item.position = estimateFromNeighbours(item, located, maxGapMs);
    }
  });

  // Day/night: use the item's own (possibly estimated) position, else the
  // centre of the located videos from the same day.
  const centreByDate = {};
  located.forEach((item) => {
    const entry = (centreByDate[item.dateKey] ||= { lat: 0, lon: 0, n: 0 });
    entry.lat += item.position.lat;
    entry.lon += item.position.lon;
    entry.n += 1;
  });
  items.forEach((item) => {
    if (!item.time) return;
    let lat = item.position?.lat;
    let lon = item.position?.lon;
    if (lat === undefined) {
      const centre = centreByDate[item.dateKey];
      if (centre) {
        lat = centre.lat / centre.n;
        lon = centre.lon / centre.n;
      }
    }
    item.period = dayOrNight(item.time, lat, lon);
  });

  return items.sort((a, b) => {
    const ta = a.time ? a.time.getTime() : Infinity;
    const tb = b.time ? b.time.getTime() : Infinity;
    return ta - tb || a.name.localeCompare(b.name);
  });
}

export function isInBounds(position, bounds) {
  if (!position || !bounds) return !!position;
  return (
    position.lat >= bounds.south &&
    position.lat <= bounds.north &&
    position.lon >= bounds.west &&
    position.lon <= bounds.east
  );
}

export function summarize(items, bounds) {
  const summary = {
    total: items.length,
    plotted: 0,
    day: 0,
    night: 0,
    unknownPeriod: 0,
    estimated: 0,
    corrupt: 0,
    noGps: 0,
    unplottable: 0,
    dbMissing: 0,
    dbPending: 0,
    dbProcessed: 0,
    dbError: 0,
    dbUnknown: 0,
  };
  const dbKey = { missing: "dbMissing", pending: "dbPending", processed: "dbProcessed", error: "dbError", unknown: "dbUnknown" };
  items.forEach((item) => {
    summary[dbKey[item.dbStatus] || "dbUnknown"] += 1;
    if (item.status === "corrupt") summary.corrupt += 1;
    if (item.status === "no-gps") summary.noGps += 1;
    if (!item.position) {
      summary.unplottable += 1;
      return;
    }
    if (!isInBounds(item.position, bounds)) return;
    summary.plotted += 1;
    if (item.position.estimated) summary.estimated += 1;
    if (item.period === "day") summary.day += 1;
    else if (item.period === "night") summary.night += 1;
    else summary.unknownPeriod += 1;
  });
  return summary;
}

// Consecutive plotted points per date, for drawing the patrol path.
export function pathsByDate(items) {
  const paths = {};
  items.forEach((item) => {
    if (!item.position || !item.time) return;
    (paths[item.dateKey] ||= []).push([item.position.lat, item.position.lon]);
  });
  return paths;
}

export function availableDates(items) {
  return [...new Set(items.map((item) => item.dateKey))].sort();
}
